import React, { useEffect, useState } from "react";

import Paper from "@mui/material/Paper";
import { Grid, Card, CardContent, Chip, Tooltip, Box, Typography, Collapse, IconButton } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { makeStyles } from 'tss-react/mui';
import { alpha } from "@mui/material/styles";
import { green, red, orange, amber, blue, purple } from "@mui/material/colors";

import Rating from '@mui/material/Rating';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import AndroidIcon from "@mui/icons-material/Android";
import StarIcon from "@mui/icons-material/Star";
import AssessmentIcon from "@mui/icons-material/Assessment";
import GroupIcon from "@mui/icons-material/Group";

import moment from "moment";
import { isArray } from "lodash";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import AuditDetailTable from "../../components/AuditDetailTable";
import PeriodSelect from "../../components/PeriodSelect";
import { rangoDePeriodo } from "../../helpers/rangoDePeriodo";
import useDashboardAuditor from "../../hooks/useDashboardAuditor";

const STAR_COLORS = {
    5: green[600],
    4: green[400],
    3: amber[700],
    2: orange[600],
    1: red[600],
};

const useStyles = makeStyles()((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "auto",
        background: theme.palette.background.default,
    },
    cardsWrap: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(2),
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
        gap: theme.spacing(1.5),
    },

    filtersBar: {
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 12,
    },
    periodSelect: {
        height: 40,
        minWidth: 150,
        padding: "0 26px 0 12px",
        fontSize: "0.875rem",
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.mode === "light" ? "rgba(0,0,0,0.23)" : "rgba(255,255,255,0.23)"}`,
        borderRadius: 4,
        cursor: "pointer",
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
        backgroundImage:
            "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%23888880' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 10px center",
        "&:hover": {
            borderColor: theme.palette.text.primary,
        },
        "&:focus": {
            outline: "none",
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
            padding: "0 25px 0 11px",
        },
    },
    tablePaper: {
        borderRadius: 12,
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        minHeight: 340,
        display: "flex",
        flexDirection: "column",
    },
    on:  { color: green[500], fontSize: 14 },
    off: { color: theme.palette.text.disabled, fontSize: 14 },
    kpiCard: {
        borderRadius: 16,
        height: "100%",
        position: "relative",
        overflow: "hidden",
        border: `1px solid ${theme.palette.divider}`,
        background: theme.palette.background.paper,
        boxShadow: theme.palette.mode === "light"
            ? "0 6px 18px rgba(0,0,0,0.05)"
            : "0 6px 18px rgba(0,0,0,0.30)",
    },
    kpiIcon: {
        position: "absolute",
        top: 12,
        right: 12,
        width: 30,
        height: 30,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "& svg": { fontSize: 17 },
    },
    kpiLabel: {
        fontSize: "0.68rem",
        textTransform: "uppercase",
        letterSpacing: 0.7,
        fontWeight: 600,
        paddingRight: 34,
    },
    kpiValue: {
        fontSize: "1.9rem",
        fontWeight: 700,
        lineHeight: 1.15,
        marginTop: 2,
    },
    kpiSub: { fontSize: "0.72rem", marginTop: 4, minHeight: 17 },
    distSegmentedBar: {
        display: "flex",
        height: 10,
        borderRadius: 6,
        overflow: "hidden",
        marginTop: 10,
        background: theme.palette.mode === "light" ? "#eceae3" : "#2b2b29",
    },

    tableHeader: {
        background: theme.palette.primary.main,
        "& th": {
            color: theme.palette.primary.contrastText,
            fontWeight: 700,
            fontSize: "0.72rem",
            padding: "6px 8px",
            whiteSpace: "nowrap",
        },
    },
    td: {
        padding: "5px 8px",
        fontSize: "0.78rem",
    },
    distBox: {
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
    },
    distItem: {
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontSize: "0.7rem",
        fontWeight: 700,
        lineHeight: 1,
    },
    muted: { color: theme.palette.text.secondary },
    faint: { color: theme.palette.text.disabled },
}));

function KpiCard({ label, value, sub, icon, color }) {
    const { classes } = useStyles();

    const sinDato = value === null || value === undefined || value === "—";

    return (
        <Card className={classes.kpiCard}>
            <CardContent style={{ padding: "14px 16px" }}>
                <Typography className={`${classes.muted} ${classes.kpiLabel}`}>
                    {label}
                </Typography>

                <Typography
                    className={classes.kpiValue}
                    style={{ color: sinDato ? undefined : color }}
                        color={sinDato ? "textSecondary" : undefined}
                >
                    {sinDato ? "—" : value}
                </Typography>

                <Typography className={`${classes.muted} ${classes.kpiSub}`}>
                    {sub ?? ""}
                </Typography>

                <Box
                    className={classes.kpiIcon}
                    style={{ color, background: alpha(color, 0.14) }}
                >
                    {icon}
                </Box>
            </CardContent>
        </Card>
    );
}

function DistBar({ dist }) {
    const { classes } = useStyles();
    if (!dist) return <span className={classes.faint}>—</span>;
    return (
        <Box className={classes.distBox}>
            {[5, 4, 3, 2, 1].map((n) => (
                <Tooltip key={n} title={`${n}★: ${dist[n] ?? 0}`}>
                    <Box className={classes.distItem} style={{ color: STAR_COLORS[n] }}>
                        <StarIcon style={{ fontSize: 9 }} />
                        <span>{dist[n] ?? 0}</span>
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
}

function TypeChips({ byType }) {
    const { classes } = useStyles();
    if (!byType) return <span className={classes.faint}>—</span>;
    return (
        <Box style={{ gap: 3 }} sx={{ display: "flex" }}>
            {byType.user > 0 && (
                <Chip
                    size="small"
                        icon={<PersonIcon style={{ fontSize: 11 }} />}
                        label={byType.user}
                    sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: (t) => alpha(blue[500], t.palette.mode === "light" ? 0.14 : 0.24), color: (t) => t.palette.mode === "light" ? blue[800] : blue[200] }}
                    />
            )}
            {byType.ia > 0 && (
                <Chip
                    size="small"
                        icon={<AndroidIcon style={{ fontSize: 11 }} />}
                        label={byType.ia}
                    sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700, bgcolor: (t) => alpha(purple[500], t.palette.mode === "light" ? 0.14 : 0.24), color: (t) => t.palette.mode === "light" ? purple[800] : purple[200] }}
                    />
            )}
        </Box>
    );
}

const CLASSIFICATION_LABELS = {
    "-1": () => i18n.t("auditor.classification.spam"),
    "-2": () => i18n.t("auditor.classification.noReply"),
    "-4": () => i18n.t("auditor.classification.escalated"),
    "-5": () => i18n.t("auditor.classification.recurring"),
    "-6": () => i18n.t("auditor.classification.unclassified"),
};

function rateDisplay(r) {
    const n = Number(r);
    if (n < 0) return (CLASSIFICATION_LABELS[String(n)]?.() ?? i18n.t("auditor.classification.noRating"));
    return null;
}

function ratingColor(r) {
    const n = Number(r);
    if (!n || n < 0) return amber[700];
    if (n >= 4) return green[600];
    if (n >= 3) return amber[700];
    return red[600];
}

export function RatingBox({ rating, id }) {
    const { classes } = useStyles();
    const label = rateDisplay(rating);
    if (label) {
        return (
            <Typography className={classes.muted} style={{ fontSize: "0.72rem", fontWeight: 700 }}>
                {label}
            </Typography>
        );
    }
    return (
        <Rating
            key={id}
                        value={rating === null ? 0 : parseFloat(rating)}
            precision={0.5}
            max={5}
            readOnly
            size="small"
                    />
    );
}

const PERIODOS_AUDITOR = [
    { valor: "hoy", etiqueta: "Hoy" },
    { valor: "ayer", etiqueta: "Ayer" },
    { valor: "semana", etiqueta: "Esta semana" },
    { valor: "semanaPasada", etiqueta: "Semana pasada" },
    { valor: "personalizado", etiqueta: "Personalizado" },
];

const Auditor = () => {
    const { classes, theme } = useStyles();

    const [attendants, setAttendants] = useState([]);
    const [iaContacts, setIaContacts] = useState([]);
    const [counters, setCounters] = useState(null);
    const [periodo, setPeriodo] = useState("hoy");
    const [dateFrom, setDateFrom] = useState(moment().format("YYYY-MM-DD"));
    const [dateTo, setDateTo]     = useState(moment().format("YYYY-MM-DD"));
    const [loading, setLoading]   = useState(false);
    const [expandedKey, setExpandedKey] = useState(null);
    const [tab, setTab] = useState(0);

    const companyId = localStorage.getItem("companyId");
    const { find }  = useDashboardAuditor();

    useEffect(() => {
        const t = setTimeout(() => fetchData(), 1000);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchData(overrides = {}) {
        setLoading(true);
        const desde = overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
        const hasta = overrides.dateTo !== undefined ? overrides.dateTo : dateTo;
        const data = await find({
            date_from: moment(desde).format("YYYY-MM-DD"),
            date_to:   moment(hasta).format("YYYY-MM-DD"),
        });
        setCounters(data.counters ?? null);
        setAttendants(isArray(data.attendants) ? data.attendants : []);
        setIaContacts(isArray(data.iaContacts) ? data.iaContacts : []);
        setLoading(false);
    }

    function handleCambioPeriodo(valor) {
        setPeriodo(valor);
        if (valor === "personalizado") return;

        const rango = rangoDePeriodo(valor);
        const nuevoDesde = rango.from.format("YYYY-MM-DD");
        const nuevoHasta = rango.to.format("YYYY-MM-DD");
        setDateFrom(nuevoDesde);
        setDateTo(nuevoHasta);
        fetchData({ dateFrom: nuevoDesde, dateTo: nuevoHasta });
    }

    function toggleExpanded(rowId) {
        const key = `${tab}:${rowId}`;
        setExpandedKey((actual) => (actual === key ? null : key));
    }

    const visibleRows = tab === 0 ? attendants : iaContacts;
    const distLabels = {
        5: i18n.t("auditor.distribution.verySatisfied"),
        4: i18n.t("auditor.distribution.satisfied"),
        3: i18n.t("auditor.distribution.neutral"),
        2: i18n.t("auditor.distribution.unsatisfied"),
        1: i18n.t("auditor.distribution.veryUnsatisfied"),
    };

    return (
        <MainContainer maxWidth={false}>
            <MainHeader>
                <Title>{i18n.t("auditor.title")}</Title>
            </MainHeader>
            <Paper className={classes.mainPaper} elevation={0}>

                <div className={classes.filtersBar}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                        <PeriodSelect
                            className={classes.periodSelect}
                            value={periodo}
                            onChange={handleCambioPeriodo}
                            opciones={PERIODOS_AUDITOR}
                        />
                        <TextField
                        label={i18n.t("auditor.filters.from")}
                            type="date"
                            size="small"
                        value={dateFrom}
                            onChange={(e) => { setPeriodo("personalizado"); setDateFrom(e.target.value); }}
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                    />
                        <TextField
                        label={i18n.t("auditor.filters.to")}
                            type="date"
                            size="small"
                        value={dateTo}
                            onChange={(e) => { setPeriodo("personalizado"); setDateTo(e.target.value); }}
                            slotProps={{
                                inputLabel: { shrink: true }
                            }}
                    />
                        <ButtonWithSpinner
                            loading={loading}
                            onClick={fetchData}
                            variant="contained"
                        color="primary"
                            size="small"
                        >
                            {i18n.t("auditor.filters.filter")}
                        </ButtonWithSpinner>
                    </Box>
                </div>

                {}
                <Box className={classes.cardsWrap}>
                    <KpiCard
                        label={i18n.t("auditor.kpis.totalAudits")}
                        value={counters?.totalAudits}
                        icon={<AssessmentIcon />}
                        color={blue[600]}
                    />
                    <KpiCard
                        label={i18n.t("auditor.kpis.agentsAudited")}
                        value={counters?.totalAgentsAudited}
                        sub={`${counters?.auditsByType?.user ?? 0} ${i18n.t("auditor.kpis.userAuditsSuffix")}`}
                        icon={<GroupIcon />}
                        color={purple[600]}
                    />
                    <KpiCard
                        label={i18n.t("auditor.kpis.aiClients")}
                        value={iaContacts.length}
                        sub={`${counters?.auditsByType?.ia ?? 0} ${i18n.t("auditor.kpis.aiAuditsSuffix")}`}
                        icon={<AndroidIcon />}
                        color={blue[700]}
                    />
                    <KpiCard
                        label={i18n.t("auditor.kpis.avgUser")}
                        value={counters?.avgRatingUser ? `${counters.avgRatingUser} ★` : "—"}
                        icon={<PersonIcon />}
                        color={ratingColor(counters?.avgRatingUser)}
                    />
                    <KpiCard
                        label={i18n.t("auditor.kpis.avgAi")}
                        value={counters?.avgRatingIa ? `${counters.avgRatingIa} ★` : "—"}
                        icon={<AndroidIcon />}
                        color={ratingColor(counters?.avgRatingIa)}
                    />

                    {}
                    {counters && (
                        <Card className={classes.kpiCard} style={{ gridColumn: "span 2" }}>
                            <CardContent style={{ padding: "14px 16px" }}>
                                <Typography className={`${classes.muted} ${classes.kpiLabel}`}>
                                    {i18n.t("auditor.distribution.title")}
                                </Typography>

                                {(() => {
                                    const valores = [5, 4, 3, 2, 1].map((n) => counters.ratingDistribution?.[n] ?? 0);
                                    const suma = valores.reduce((a, b) => a + b, 0);

                                    return (
                                        <>
                                            <Box className={classes.distSegmentedBar}>
                                                {[5, 4, 3, 2, 1].map((n, i) => {
                                                    const total = valores[i];
                                                    if (total <= 0) return null;
                                                    const pct = suma > 0 ? (total / suma) * 100 : 0;

                                                    return (
                                                        <Tooltip key={n} title={`${distLabels[n]}: ${total} (${Math.round(pct)}%)`}>
                                                            <Box
                                                                style={{ width: `${pct}%`, background: STAR_COLORS[n] }}
                                                            />
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Box>

                                            <Box className={classes.distBox} style={{ marginTop: 10 }}>
                                                {[5, 4, 3, 2, 1].map((n) => (
                                                    <Tooltip key={n} title={distLabels[n]}>
                                                        <Box className={classes.distItem} style={{ color: STAR_COLORS[n] }}>
                                                            <StarIcon style={{ fontSize: 10 }} />
                                                            <span>{counters.ratingDistribution?.[n] ?? 0}</span>
                                                        </Box>
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        </>
                                    );
                                })()}
                            </CardContent>
                        </Card>
                    )}
                </Box>

                {}
                <Paper className={classes.tablePaper} elevation={0}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        indicatorColor="primary"
                        textColor="primary"
                        style={{ borderBottom: `1px solid ${theme.palette.divider}` }}
                    >
                        {}
                        <Tab label={`👤 ${i18n.t("auditor.tabs.user")} (${counters?.auditsByType?.user ?? 0})`} />
                        <Tab label={`🤖 ${i18n.t("auditor.tabs.ai")} (${counters?.auditsByType?.ia ?? 0})`} />
                    </Tabs>
                <TableContainer>
                    <Table size="small">
                        <TableHead className={classes.tableHeader}>
                            <TableRow>
                                <TableCell>{tab === 0 ? i18n.t("auditor.table.agent") : i18n.t("auditor.table.client")}</TableCell>
                                <TableCell align="center">{i18n.t("auditor.table.rating")}</TableCell>
                                <TableCell align="center">{i18n.t("auditor.table.auditsTickets")}</TableCell>
                                <TableCell align="center">{i18n.t("auditor.table.type")}</TableCell>
                                <TableCell align="center">{i18n.t("auditor.table.lastAudit")}</TableCell>
                                <TableCell align="center">{i18n.t("auditor.table.view")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" className={`${classes.td} ${classes.faint}`} style={{ padding: 24 }}>
                                        {i18n.t("auditor.table.empty")}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                visibleRows.map((a, k) => {
                                    const rowKey = `${tab}:${a.id}`;
                                    const isExpanded = expandedKey === rowKey;
                                    const puedeExpandir = a.id !== null && a.id !== undefined;

                                    return (
                                    <React.Fragment key={k}>
                                    <TableRow hover selected={isExpanded}>

                                        {}
                                        <TableCell className={classes.td}>
                                            <Box
                                                style={{ gap: 4 }}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center"
                                                }}>
                                                {tab === 0 && (a.online
                                                    ? <CheckCircleIcon className={classes.on} />
                                                    : <ErrorIcon className={classes.off} />
                                                )}
                                                <Box>
                                                    <span style={{ fontWeight: 600 }}>{a.name}</span>
                                                    {tab === 1 && a.phone && (
                                                        <Typography className={classes.muted} style={{ fontSize: "0.7rem" }}>
                                                            {a.phone}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>

                                        {}
                                        <TableCell align="center" className={classes.td}>
                                            <RatingBox rating={a.rating} id={a.id} />
                                            {!rateDisplay(a.rating) && (
                                                <Typography style={{ fontSize: "0.7rem", fontWeight: 700, color: ratingColor(a.rating) }}>
                                                    {a.rating ?? 0} / 5
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {}
                                        <TableCell align="center" className={classes.td}>
                                            <Typography style={{ fontSize: "0.78rem", fontWeight: 700 }}>
                                                {a.auditCount ?? 0} / {a.auditedTickets ?? 0}
                                            </Typography>
                                        </TableCell>

                                        {}
                                        <TableCell align="center" className={classes.td}>
                                            {tab === 0
                                                ? <TypeChips byType={a.auditsByType} />
                                                : <Chip size="small" icon={<AndroidIcon style={{ fontSize: 13 }} />} label={i18n.t("auditor.table.aiChip")} sx={{ height: 20, fontSize: "0.72rem", fontWeight: 700, bgcolor: (t) => alpha(purple[500], t.palette.mode === "light" ? 0.14 : 0.24), color: (t) => t.palette.mode === "light" ? purple[800] : purple[200] }} />
                                            }
                                        </TableCell>

                                        {}
                                        <TableCell align="center" className={classes.td}>
                                            <Typography style={{ fontSize: "0.72rem" }}>
                                                {a.lastAuditAt ? moment(a.lastAuditAt).format("DD/MM/YY HH:mm") : "—"}
                                            </Typography>
                                        </TableCell>

                                        {}
                                        <TableCell align="center" className={classes.td}>
                                            <Tooltip title={puedeExpandir ? (isExpanded ? "Ocultar" : "Ver auditorías") : "Sin agente asociado"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        disabled={!puedeExpandir}
                                                        onClick={() => toggleExpanded(a.id)}
                                                    >
                                                        <ExpandMoreIcon
                                                            style={{
                                                                fontSize: 20,
                                                                transition: "transform .15s ease",
                                                                transform: isExpanded ? "rotate(180deg)" : "none",
                                                            }}
                                                        />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>

                                    </TableRow>
                                    {isExpanded && (
                                        <TableRow>
                                            <TableCell colSpan={6} style={{ padding: 0, background: theme.palette.mode === "light" ? "#f7f7f9" : "#1a1d23" }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box style={{ padding: 8 }}>
                                                        <AuditDetailTable
                                                            companyId={companyId}
                                                            idUser={tab === 0 ? a.id : undefined}
                                                            contactId={tab === 1 ? a.id : undefined}
                                                            mode={tab === 0 ? "user" : "ia"}
                                                            date_from={dateFrom}
                                                            date_to={dateTo}
                                                        />
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    </React.Fragment>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Paper>
            </Paper>
        </MainContainer>
    );
};

export default Auditor;
